import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowRight, FiCheck } from 'react-icons/fi';
import { useWeb3 } from '../context/Web3Context';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'react-toastify';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MintingSection from '../components/MintingSection';
import PDFViewer from '../components/PDFViewer';
import WalletConnector from '../components/WalletConnector';
import AddToWallet from '../components/AddToWallet';

import LANDING_COPY from '../components/landing/landingCopy';
import {
  Reveal,
  CountUp,
  useParallax,
  useMouseTilt,
  useScrollProgress,
} from '../components/landing/landingFx';
import {
  BrowserFrame,
  PhoneFrame,
  DashboardMock,
  SmmPanelMock,
  MarketplaceMock,
  CasinoMock,
} from '../components/landing/ShowcaseMockups';
import PlatformStrip from '../components/landing/PlatformStrip';
import ImmersiveHUD from '../components/landing/ImmersiveHUD';

import '../styles/theme.css';
import './LandingPage.css';

// Real WebGL (three.js) hero scene — lazy-loaded so the ~600KB three.js chunk
// ships only with the landing page, not the rest of the app's routes.
const WebGLScene = lazy(() => import('../components/landing/WebGLScene'));

// Scene rail "chapters" — section ids the HUD tracks + jumps between.
const SCENES = [
  { id: 'hero', label: 'Enter' },
  { id: 'platforms', label: 'Platforms' },
  { id: 'explore', label: 'Product' },
  { id: 'how', label: 'How it works' },
  { id: 'join', label: 'Join' },
];

/* FAQ accordion item */
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`}>
      <button
        className="faq-question"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span>{question}</span>
        <span className="faq-icon">{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && (
        <div className="faq-answer">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

/* Alternating product showcase row: copy on one side, live mock on the other */
const ShowcaseRow = ({
  reverse,
  accent,
  eyebrow,
  title,
  body,
  bullets,
  url,
  frame = 'browser',
  children,
  onConnect,
  ctaLabel,
}) => {
  const parallaxRef = useParallax(0.06);
  return (
    <div className={`neo-show-row accent-${accent} ${reverse ? 'reverse' : ''}`}>
      <Reveal className="neo-show-copy" y={36}>
        <span className={`neo-eyebrow accent-${accent}`}>{eyebrow}</span>
        <h3 className="neo-show-title">{title}</h3>
        <p className="neo-show-body">{body}</p>
        <ul className="neo-show-bullets">
          {bullets.map((b) => (
            <li key={b}>
              <FiCheck className="neo-bullet-ic" aria-hidden="true" />
              {b}
            </li>
          ))}
        </ul>
        <button className="neo-link-btn" type="button" onClick={onConnect}>
          {ctaLabel} <FiArrowRight aria-hidden="true" />
        </button>
      </Reveal>

      <Reveal className="neo-show-device" y={48} delay={120}>
        <div className="neo-device-parallax" ref={parallaxRef}>
          {frame === 'phone' ? (
            <PhoneFrame accent={accent}>{children}</PhoneFrame>
          ) : (
            <BrowserFrame accent={accent} label={url}>
              {children}
            </BrowserFrame>
          )}
        </div>
      </Reveal>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { isConnected, account } = useWeb3();
  const { login, isAuthenticated } = useAuth();
  const { language, t } = useLanguage();
  const [showWalletConnector, setShowWalletConnector] = useState(false);

  const copy = LANDING_COPY[language] || LANDING_COPY.en;
  const progress = useScrollProgress();
  const heroTiltRef = useMouseTilt(7);

  const getPDFPath = (baseName) => `/${baseName}-${language}.pdf`;

  /* ---- SEO: inject FAQ + Product structured data, sharpen title ---- */
  useEffect(() => {
    const prevTitle = document.title;
    document.title =
      'PHONESTREAM — Blockchain Music Streaming, NFTs, SMM & Casino on BSC';

    const faq = [];
    for (let i = 1; i <= 14; i += 1) {
      const q = t(`faqQ${i}`);
      const a = t(`faqA${i}`);
      if (q && a && q !== `faqQ${i}`) {
        faq.push({
          '@type': 'Question',
          name: q,
          acceptedAnswer: { '@type': 'Answer', text: a },
        });
      }
    }

    const graph = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          '@id': 'https://phonestream.online/#website',
          url: 'https://phonestream.online/',
          name: 'PHONESTREAM',
          description:
            'Blockchain music streaming revenue, generative NFTs, SMM marketing and a USDT casino on Binance Smart Chain.',
          inLanguage: language === 'es' ? 'es' : 'en',
        },
        {
          '@type': 'Product',
          name: 'PHONESTREAM NFT',
          description:
            'A generative NFT representing ownership of a 24/7 streaming machine that earns real USDT revenue on Binance Smart Chain.',
          brand: { '@type': 'Brand', name: 'PHONESTREAM' },
          category: 'NFT / Web3',
          offers: {
            '@type': 'Offer',
            priceCurrency: 'USDT',
            price: '900',
            availability: 'https://schema.org/InStock',
            url: 'https://phonestream.online/marketplace',
          },
        },
        ...(faq.length
          ? [{ '@type': 'FAQPage', mainEntity: faq }]
          : []),
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'ps-landing-jsonld';
    script.text = JSON.stringify(graph);
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      const el = document.getElementById('ps-landing-jsonld');
      if (el) el.remove();
    };
  }, [language, t]);

  const handleConnectWallet = () => setShowWalletConnector(true);
  const handleWalletConnected = () => navigate('/dashboard');

  const handleGoToDashboard = async () => {
    if (!isConnected) {
      toast.error('Please connect your wallet first');
      return;
    }
    if (!isAuthenticated) {
      try {
        await login();
        navigate('/dashboard');
      } catch (error) {
        console.error('Login error:', error);
        toast.error('Please sign the message in your wallet to access the dashboard');
        navigate('/dashboard');
      }
    } else {
      navigate('/dashboard');
    }
  };

  const heroTitleLines = (copy.heroTitle || '').split('\n');

  return (
    <div className="landing-page neo">
      {/* top neon scroll progress */}
      <div className="neo-scroll-progress" style={{ transform: `scaleX(${progress})` }} />

      {/* immersive game HUD: boot intro, frame, reticle, scene rail */}
      <ImmersiveHUD scenes={SCENES} />

      <Header />

      <main className="landing-main">
        {/* ============================ HERO ============================ */}
        <section className="neo-hero" id="hero" aria-label="PHONESTREAM">
          <div className="neo-hero-bg" aria-hidden="true">
            <div className="neo-grid" />
            <Suspense fallback={null}>
              <WebGLScene />
            </Suspense>
            <div className="neo-orb orb-cyan" />
            <div className="neo-orb orb-magenta" />
            <div className="neo-orb orb-gold" />
            <div className="neo-beam" />
          </div>

          <div className="neo-hero-inner">
            <div className="neo-hero-copy">
              <span className="neo-eyebrow accent-cyan neo-pulse">{copy.heroEyebrow}</span>
              <h1 className="neo-hero-title">
                {heroTitleLines.map((line, i) => (
                  <span className="neo-hero-line" key={i}>
                    {line}
                  </span>
                ))}
              </h1>
              <p className="neo-hero-lede">{copy.heroLede}</p>

              <div className="neo-cta-row">
                <button className="neo-btn neo-btn-primary" onClick={handleConnectWallet}>
                  <span className="neo-btn-shine" />
                  {isConnected
                    ? `${account?.substring(0, 6)}...${account?.substring(account.length - 4)}`
                    : copy.ctaConnect}
                  <FiArrowRight aria-hidden="true" />
                </button>
                {(isConnected || isAuthenticated) ? (
                  <button className="neo-btn neo-btn-ghost" onClick={handleGoToDashboard}>
                    {t('goToDashboard')}
                  </button>
                ) : (
                  <a className="neo-btn neo-btn-ghost" href="#explore">
                    {copy.ctaExplore}
                  </a>
                )}
              </div>

              <span className="neo-hero-note">{copy.heroNote}</span>

              <div className="neo-hero-add">
                <AddToWallet />
              </div>
            </div>

            <Reveal className="neo-hero-device" y={0} delay={150}>
              <div className="neo-tilt" ref={heroTiltRef}>
                <BrowserFrame accent="cyan" label="phonestream.online/dashboard">
                  <DashboardMock />
                </BrowserFrame>
                <div className="neo-float-badge badge-1">+418.20 USDT<em>stream revenue</em></div>
                <div className="neo-float-badge badge-2">2.50× <em>cashed out</em></div>
              </div>
            </Reveal>
          </div>

          {/* marquee */}
          <div className="neo-marquee" aria-hidden="true">
            <div className="neo-marquee-track">
              {Array.from({ length: 2 }).map((_, dup) => (
                <React.Fragment key={dup}>
                  {['STREAMING REVENUE', 'GENERATIVE NFTs', 'USDT ESCROW', 'SMM ENGINE',
                    'NEON CASINO', 'BSC MAINNET', 'SPOTIFY DATA', 'PASSIVE INCOME'].map((w) => (
                    <span key={`${dup}-${w}`}>
                      {w} <i>◆</i>
                    </span>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        {/* ====================== LIVE STAT BAND ======================= */}
        <section className="neo-stats" aria-label="Platform stats">
          <div className="neo-stats-inner">
            <Reveal className="neo-stat" y={20}>
              <b className="neo-stat-num">
                <CountUp value={2.41} decimals={2} prefix="$" suffix="M" />
              </b>
              <em>{copy.statVolume}</em>
            </Reveal>
            <Reveal className="neo-stat" y={20} delay={80}>
              <b className="neo-stat-num">
                <CountUp value={1000} />
              </b>
              <em>{copy.statSupply}</em>
            </Reveal>
            <Reveal className="neo-stat" y={20} delay={160}>
              <b className="neo-stat-num">
                <CountUp value={10} suffix="+" />
              </b>
              <em>{copy.statGames}</em>
            </Reveal>
            <Reveal className="neo-stat" y={20} delay={240}>
              <b className="neo-stat-num">
                <CountUp value={12} suffix="+" />
              </b>
              <em>{copy.statPlatforms}</em>
            </Reveal>
          </div>
          <div className="neo-stats-chips">
            <span>⛓ {copy.statChain}: <b>BSC</b></span>
            <span>🟢 {copy.statUptime}: <b>99.9%</b></span>
            <span>💵 USDT · 18 decimals</span>
          </div>
        </section>

        {/* ===================== PLATFORMS WE GROW ===================== */}
        <section className="neo-platforms" id="platforms">
          <Reveal className="neo-platforms-inner" y={20}>
            <span className="neo-eyebrow accent-magenta">{copy.platformsEyebrow}</span>
            <h2 className="neo-h2">{copy.platformsTitle}</h2>
            <PlatformStrip />
          </Reveal>
        </section>

        {/* ===================== SHOWCASE INTRO ======================= */}
        <section className="neo-show" id="explore">
          <Reveal className="neo-section-head" y={24}>
            <span className="neo-eyebrow accent-magenta">{copy.showcaseEyebrow}</span>
            <h2 className="neo-h2">{copy.showcaseTitle}</h2>
            <p className="neo-sub">{copy.showcaseSubtitle}</p>
          </Reveal>

          <ShowcaseRow
            accent="cyan"
            url="phonestream.online/dashboard"
            eyebrow={copy.dashEyebrow}
            title={copy.dashTitle}
            body={copy.dashBody}
            bullets={[copy.dashB1, copy.dashB2, copy.dashB3, copy.dashB4]}
            onConnect={handleConnectWallet}
            ctaLabel={copy.ctaConnect}
          >
            <DashboardMock />
          </ShowcaseRow>

          <ShowcaseRow
            reverse
            accent="magenta"
            url="phonestream.online/store"
            eyebrow={copy.smmEyebrow}
            title={copy.smmTitle}
            body={copy.smmBody}
            bullets={[copy.smmB1, copy.smmB2, copy.smmB3, copy.smmB4]}
            onConnect={handleConnectWallet}
            ctaLabel={copy.ctaConnect}
          >
            <SmmPanelMock />
          </ShowcaseRow>

          <ShowcaseRow
            accent="gold"
            url="phonestream.online/marketplace"
            eyebrow={copy.mktEyebrow}
            title={copy.mktTitle}
            body={copy.mktBody}
            bullets={[copy.mktB1, copy.mktB2, copy.mktB3, copy.mktB4]}
            onConnect={handleConnectWallet}
            ctaLabel={copy.ctaConnect}
          >
            <MarketplaceMock />
          </ShowcaseRow>

          <ShowcaseRow
            reverse
            accent="green"
            url="phonestream.online/play"
            frame="phone"
            eyebrow={copy.casEyebrow}
            title={copy.casTitle}
            body={copy.casBody}
            bullets={[copy.casB1, copy.casB2, copy.casB3, copy.casB4]}
            onConnect={handleConnectWallet}
            ctaLabel={copy.ctaConnect}
          >
            <CasinoMock />
          </ShowcaseRow>
        </section>

        {/* ======================== BENEFITS ========================== */}
        <section className="benefits-section neo-block">
          <Reveal className="neo-section-head" y={20}>
            <span className="neo-eyebrow accent-cyan">PHONESTREAM</span>
            <h2 className="neo-h2">{t('whyChoose')}</h2>
          </Reveal>
          <div className="benefits-grid">
            {[
              ['🎵', t('benefit1Title'), t('benefit1Desc')],
              ['💰', t('benefit2Title'), t('benefit2Desc')],
              ['📱', t('benefit3Title'), t('benefit3Desc')],
              ['🔒', t('benefit4Title'), t('benefit4Desc')],
            ].map(([icon, title, desc], i) => (
              <Reveal className="benefit-card" key={title} y={28} delay={i * 80}>
                <div className="benefit-icon">{icon}</div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ====================== HOW IT WORKS ======================== */}
        <section className="neo-how" id="how">
          <Reveal className="neo-section-head" y={20}>
            <span className="neo-eyebrow accent-green">{copy.howEyebrow}</span>
            <h2 className="neo-h2">{copy.howTitle}</h2>
            <p className="neo-sub">{copy.howSubtitle}</p>
          </Reveal>
          <div className="neo-steps">
            {[
              ['01', copy.step1Title, copy.step1Body],
              ['02', copy.step2Title, copy.step2Body],
              ['03', copy.step3Title, copy.step3Body],
              ['04', copy.step4Title, copy.step4Body],
            ].map(([n, title, body], i) => (
              <Reveal className="neo-step" key={n} y={30} delay={i * 90}>
                <span className="neo-step-num">{n}</span>
                <h3>{title}</h3>
                <p>{body}</p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ========================= MINTING ========================== */}
        <MintingSection />

        {/* ========================= FEATURES ========================= */}
        <section className="features-section">
          <Reveal className="neo-section-head" y={20}>
            <h2 className="neo-h2">{t('features')}</h2>
          </Reveal>
          <div className="features-list">
            {[
              [t('feature1Title'), t('feature1Desc')],
              [t('feature2Title'), t('feature2Desc')],
              [t('feature3Title'), t('feature3Desc')],
              [t('feature4Title'), t('feature4Desc')],
            ].map(([title, desc], i) => (
              <Reveal className="feature-item" key={title} y={24} delay={i * 70}>
                <h3>{title}</h3>
                <p>{desc}</p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ======================== PDF GUIDES ======================== */}
        <PDFViewer
          pdfPath={getPDFPath('guide')}
          title={t('guide')}
          viewButtonText={t('viewGuide')}
          hideButtonText={t('hideGuide')}
        />
        <PDFViewer
          pdfPath={getPDFPath('whitepaper')}
          title={t('whitepaper')}
          allowDownload={true}
          viewButtonText={t('viewWhitepaper')}
          hideButtonText={t('hideWhitepaper')}
        />
        <PDFViewer
          pdfPath={getPDFPath('pitchdeck')}
          title={t('pitchDeck')}
          viewButtonText={t('viewPitchDeck')}
          hideButtonText={t('hidePitchDeck')}
        />

        {/* ========================= ABOUT US ========================= */}
        <section className="about-us-section">
          <div className="about-us-content">
            <Reveal className="neo-section-head" y={20}>
              <h2 className="neo-h2">{t('aboutUs')}</h2>
            </Reveal>
            <div className="about-us-text">
              <p className="about-us-intro">{t('aboutUsWelcome')}</p>
              <Reveal className="about-us-mission" y={22}>
                <h3>{t('aboutUsMission')}</h3>
                <p>{t('aboutUsMissionText')}</p>
              </Reveal>
              <Reveal className="about-us-offer" y={22} delay={70}>
                <h3>{t('aboutUsOffer')}</h3>
                <ul>
                  <li>{t('aboutUsOffer1')}</li>
                  <li>{t('aboutUsOffer2')}</li>
                  <li>{t('aboutUsOffer3')}</li>
                  <li>{t('aboutUsOffer4')}</li>
                </ul>
              </Reveal>
              <Reveal className="about-us-join" y={22} delay={140}>
                <h3>{t('aboutUsJoin')}</h3>
                <p>{t('aboutUsJoinText')}</p>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ========================= PARTNERS ========================= */}
        <section className="partners-section">
          <div className="partners-content">
            <Reveal className="neo-section-head" y={18}>
              <h2 className="neo-h2">{t('partners')}</h2>
            </Reveal>
            <div className="partners-grid">
              <a
                href="https://www.pinksale.finance"
                target="_blank"
                rel="noopener noreferrer"
                className="partner-logo"
                title="PinkSale"
              >
                <img
                  src="/partners/pinksale-logo.png"
                  alt="PinkSale"
                  className="partner-img"
                  onError={(e) => {
                    if (!e.target.src.includes('pinksale.finance')) {
                      e.target.src = 'https://www.pinksale.finance/static/media/logo_light.9e9f9ba8.svg';
                    } else {
                      e.target.style.display = 'none';
                      const span = e.target.nextElementSibling;
                      if (span) span.style.display = 'block';
                    }
                  }}
                />
                <span className="partner-fallback" style={{ display: 'none', color: 'var(--text-primary)', fontSize: '1.5rem', fontWeight: '600' }}>PinkSale</span>
              </a>
              <a
                href="https://dexview.com"
                target="_blank"
                rel="noopener noreferrer"
                className="partner-logo"
                title="DEXView"
              >
                <img
                  src="/partners/dexview-logo.png"
                  alt="DEXView"
                  className="partner-img"
                  onError={(e) => {
                    if (!e.target.src.includes('dexview.com')) {
                      e.target.src = 'https://dexview.com/logo.png';
                    } else {
                      e.target.style.display = 'none';
                      const span = e.target.nextElementSibling;
                      if (span) span.style.display = 'block';
                    }
                  }}
                />
                <span className="partner-fallback" style={{ display: 'none', color: 'var(--text-primary)', fontSize: '1.5rem', fontWeight: '600' }}>DEXView</span>
              </a>
            </div>
          </div>
        </section>

        {/* =========================== FAQ =========================== */}
        <section className="faq-section">
          <div className="faq-content">
            <Reveal className="neo-section-head" y={18}>
              <h2 className="neo-h2">{t('faq')}</h2>
            </Reveal>
            <p className="faq-intro">{t('faqIntro')}</p>

            <div className="faq-categories">
              <div className="faq-category">
                <h3 className="faq-category-title">{t('faqGeneral')}</h3>
                <FAQItem question={t('faqQ1')} answer={t('faqA1')} />
                <FAQItem question={t('faqQ2')} answer={t('faqA2')} />
                <FAQItem question={t('faqQ3')} answer={t('faqA3')} />
              </div>
              <div className="faq-category">
                <h3 className="faq-category-title">{t('faqNFT')}</h3>
                <FAQItem question={t('faqQ4')} answer={t('faqA4')} />
                <FAQItem question={t('faqQ5')} answer={t('faqA5')} />
                <FAQItem question={t('faqQ6')} answer={t('faqA6')} />
              </div>
              <div className="faq-category">
                <h3 className="faq-category-title">{t('faqRevenue')}</h3>
                <FAQItem question={t('faqQ7')} answer={t('faqA7')} />
                <FAQItem question={t('faqQ8')} answer={t('faqA8')} />
                <FAQItem question={t('faqQ9')} answer={t('faqA9')} />
              </div>
              <div className="faq-category">
                <h3 className="faq-category-title">{t('faqCommunity')}</h3>
                <FAQItem question={t('faqQ10')} answer={t('faqA10')} />
                <FAQItem question={t('faqQ11')} answer={t('faqA11')} />
              </div>
              <div className="faq-category">
                <h3 className="faq-category-title">{t('faqTechnical')}</h3>
                <FAQItem question={t('faqQ12')} answer={t('faqA12')} />
                <FAQItem question={t('faqQ13')} answer={t('faqA13')} />
                <FAQItem question={t('faqQ14')} answer={t('faqA14')} />
              </div>
            </div>

            <p className="faq-join-discord">{t('faqJoinDiscord')}</p>
          </div>
        </section>

        {/* ======================== FINAL CTA ========================= */}
        <section className="neo-final" id="join">
          <div className="neo-final-bg" aria-hidden="true">
            <div className="neo-orb orb-cyan" />
            <div className="neo-orb orb-magenta" />
          </div>
          <Reveal className="neo-final-inner" y={28}>
            <span className="neo-eyebrow accent-gold">{copy.finalEyebrow}</span>
            <h2 className="neo-final-title">{copy.finalTitle}</h2>
            <p className="neo-final-body">{copy.finalBody}</p>
            <button className="neo-btn neo-btn-primary neo-btn-lg" onClick={handleConnectWallet}>
              <span className="neo-btn-shine" />
              {copy.finalCta} <FiArrowRight aria-hidden="true" />
            </button>
            <span className="neo-final-sub">{copy.finalSub}</span>
          </Reveal>
        </section>
      </main>

      <Footer />

      {showWalletConnector && (
        <WalletConnector
          onClose={() => setShowWalletConnector(false)}
          onConnected={handleWalletConnected}
        />
      )}
    </div>
  );
};

export default LandingPage;
